// ShareBite impact meal aggregation (Task 7.1).
//
// Pure aggregation used by impact and analytics surfaces to compute the total
// number of meals contributed. Only Donations whose status is exactly
// "Completed" count toward the impact total; every other status — Draft,
// Available, Reserved, Collected, Expired, and Cancelled — contributes nothing.
// Each Completed Donation is counted exactly once.
//
// The function is platform-independent and side-effect free; callers
// (AnalyticsService callables, dashboard views) pass in a snapshot of the
// Donations they wish to aggregate.
//
// Design reference: design.md "Property 10: Impact aggregation is
// Completed-only and counted once".
// Requirements:
//   24.4 - Impact totals reflect only Completed donations.
//   24.5 - Each Completed donation is counted exactly once.
//   30.1 - Badge/impact metrics derive from Completed-only meal totals.
//   31.1 - Aggregate totals are whole numbers greater than or equal to zero.
//   31.5 - Non-Completed donations are excluded from impact meal totals.
/**
 * Sum the Meal Counts over exactly the Completed Donations.
 *
 * For the supplied collection, returns the total of `mealCount` taken over
 * every element whose `status` is exactly `"Completed"`, each counted once.
 * Donations in any other status (`"Draft"`, `"Available"`, `"Reserved"`,
 * `"Collected"`, `"Expired"`, `"Cancelled"`) are excluded and contribute
 * nothing to the total.
 *
 * This function is pure: it reads only its argument and returns a number
 * without mutating any input or producing side effects. The result is a
 * non-negative integer whenever every Completed Donation carries a
 * non-negative integer Meal Count (Req 31.1); an empty collection (or one with
 * no Completed Donations) yields `0`.
 *
 * @param donations The collection of Donations (or projections) to aggregate.
 * @returns The total Meal Count over Completed Donations only.
 */
export function totalImpactMeals(donations) {
    let total = 0;
    for (const donation of donations) {
        if (donation.status === "Completed") {
            total += donation.mealCount;
        }
    }
    return total;
}
//# sourceMappingURL=impactAggregation.js.map